/**
 * csvBarParser — flexible CSV → Bar[] converter.
 *
 * Designed to consume the wildly different export formats trading platforms
 * use without needing platform-specific parsers everywhere. Supports:
 *
 *   NinjaTrader        — `Date Time;Open;High;Low;Close;Volume`
 *   TradingView        — `time,open,high,low,close,volume` (Unix sec or ISO)
 *   MetaTrader         — `Date,Time,Open,High,Low,Close,Volume`
 *   Tradovate          — `BarStart,Open,High,Low,Close,Volume`
 *   Generic            — any CSV with recognizable column names
 *
 * Auto-detects:
 *   - Delimiter: comma, semicolon, or tab
 *   - Header row (column names case-insensitive)
 *   - Time column shape (Unix sec, Unix ms, ISO, "MM/DD/YYYY HH:MM:SS",
 *     or split Date+Time columns)
 *
 * Output: Bar[] = `[{ time: number /* unix sec * /, open, high, low, close, volume }]`
 * Sorted ascending by time. Bars with invalid OHLC are dropped silently.
 */

const TIME_COLS = ['time', 'timestamp', 'datetime', 'date time', 'bar', 'barstart', 'bar start'];
const DATE_COLS = ['date'];
const TIME_ONLY_COLS = ['time'];
const OPEN_COLS = ['open', 'o'];
const HIGH_COLS = ['high', 'h'];
const LOW_COLS  = ['low',  'l'];
const CLOSE_COLS = ['close', 'c'];
const VOLUME_COLS = ['volume', 'vol', 'v', 'totalvolume'];

/** Detect delimiter by sampling the first non-blank line. */
const detectDelimiter = (line) => {
  const counts = {
    ',': (line.match(/,/g) || []).length,
    ';': (line.match(/;/g) || []).length,
    '\t': (line.match(/\t/g) || []).length,
  };
  let best = ',';
  let bestN = -1;
  for (const [d, n] of Object.entries(counts)) {
    if (n > bestN) { best = d; bestN = n; }
  }
  return best;
};

/** Trim + lowercase header cells for matching */
const normHeader = (s) => String(s || '').replace(/^"|"$/g, '').trim().toLowerCase();

/** Find the index of the first column whose normalized name is in `candidates` */
const findCol = (headers, candidates) => {
  for (let i = 0; i < headers.length; i++) {
    if (candidates.includes(headers[i])) return i;
  }
  return -1;
};

/** Parse a time cell into Unix seconds. Returns null if unparseable. */
const parseTimeCell = (cell, dateCell) => {
  if (cell == null || cell === '') return null;
  const raw = String(cell).trim().replace(/^"|"$/g, '');

  // Pure numeric — Unix seconds or milliseconds
  if (/^\d{9,15}$/.test(raw)) {
    const n = Number(raw);
    return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
  }

  // If we have a separate date cell, combine them
  if (dateCell != null && dateCell !== '') {
    const dateStr = String(dateCell).trim().replace(/^"|"$/g, '');
    const combined = `${dateStr} ${raw}`;
    const ms = Date.parse(combined);
    if (Number.isFinite(ms)) return Math.floor(ms / 1000);
    // Try MM/DD/YYYY explicitly
    const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) {
      const yyyy = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
      const iso = `${yyyy}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}T${raw}`;
      const ms2 = Date.parse(iso);
      if (Number.isFinite(ms2)) return Math.floor(ms2 / 1000);
    }
    return null;
  }

  // Single combined timestamp — try ISO first, then common alternates
  let ms = Date.parse(raw);
  if (Number.isFinite(ms)) return Math.floor(ms / 1000);

  // "YYYYMMDD HHMMSS" (some NT exports)
  const m = raw.match(/^(\d{4})(\d{2})(\d{2})[ T](\d{2})(\d{2})(\d{2})$/);
  if (m) {
    const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
    ms = Date.parse(iso);
    if (Number.isFinite(ms)) return Math.floor(ms / 1000);
  }

  // "MM/DD/YYYY HH:MM:SS" or with AM/PM
  const m2 = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(.+)$/);
  if (m2) {
    const yyyy = m2[3].length === 2 ? 2000 + parseInt(m2[3], 10) : parseInt(m2[3], 10);
    const iso = `${yyyy}-${m2[1].padStart(2, '0')}-${m2[2].padStart(2, '0')}T${m2[4]}`;
    ms = Date.parse(iso);
    if (Number.isFinite(ms)) return Math.floor(ms / 1000);
  }

  return null;
};

const numOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = parseFloat(String(v).replace(/^"|"$/g, '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * Parse a CSV string into normalized Bar[].
 *
 * @param {string} csvText
 * @param {Object} [opts]
 * @param {string} [opts.label]  Source label for debug logs (e.g. file path)
 * @returns {{ bars: Bar[], parsed: number, skipped: number, error?: string }}
 */
export const parseCsvToBars = (csvText, opts = {}) => {
  const label = opts.label || 'csv';
  if (!csvText || typeof csvText !== 'string') {
    return { bars: [], parsed: 0, skipped: 0, error: 'empty-input' };
  }

  // Split lines, strip BOM + blank lines + comment lines
  const lines = csvText
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith('#'));

  if (lines.length < 2) {
    // One-line headerless inputs are valid (a single bar has no header) — only
    // bail on truly empty input.
    if (lines.length === 0) {
      return { bars: [], parsed: 0, skipped: 0, error: 'no-data-rows' };
    }
  }

  const delim = detectDelimiter(lines[0]);
  const headerCells = lines[0].split(delim).map(normHeader);

  // Detect headerless inputs: NT's native bar-cache export ("Save As" from a
  // chart) writes no header row, just `YYYYMMDD HHMMSS;O;H;L;C;V` lines. If
  // the first cell of the first line is a parseable timestamp, treat the file
  // as headerless and assume the canonical column order. We don't try to
  // normalize variant orders here — every NT export uses the same layout, and
  // anyone hand-crafting a CSV can add headers if they want a different shape.
  const headerless = parseTimeCell(headerCells[0]) != null;

  // Resolve column positions. For headerless files we use the canonical NT
  // export layout: time;open;high;low;close;volume.
  let colTime, colDate, colTimeOnly, colOpen, colHigh, colLow, colClose, colVolume;
  if (headerless) {
    colTime = 0; colDate = -1; colTimeOnly = -1;
    colOpen = 1; colHigh = 2; colLow = 3; colClose = 4;
    // Volume is optional — if the row has only 5 cells, that's fine.
    colVolume = headerCells.length >= 6 ? 5 : -1;
  } else {
    colTime    = findCol(headerCells, TIME_COLS);
    colDate    = findCol(headerCells, DATE_COLS);
    // If we found both 'date' and 'time' as separate columns, the latter is the time-of-day
    colTimeOnly = (colDate >= 0 && colTime < 0) ? findCol(headerCells, TIME_ONLY_COLS) : -1;
    colOpen    = findCol(headerCells, OPEN_COLS);
    colHigh    = findCol(headerCells, HIGH_COLS);
    colLow     = findCol(headerCells, LOW_COLS);
    colClose   = findCol(headerCells, CLOSE_COLS);
    colVolume  = findCol(headerCells, VOLUME_COLS);

    // Time can come from either a single timestamp column OR date+time pair
    const hasTime = colTime >= 0 || (colDate >= 0 && colTimeOnly >= 0) || colDate >= 0;
    if (!hasTime || colOpen < 0 || colHigh < 0 || colLow < 0 || colClose < 0) {
      return {
        bars: [], parsed: 0, skipped: lines.length - 1,
        error: `missing-required-columns (got: ${headerCells.join(', ')})`
      };
    }
  }

  const bars = [];
  let skipped = 0;
  // Headerless files have a data row at index 0; headered files start at 1.
  const startIdx = headerless ? 0 : 1;

  for (let i = startIdx; i < lines.length; i++) {
    const cells = lines[i].split(delim);

    let timeSec;
    if (colTime >= 0) {
      timeSec = parseTimeCell(cells[colTime]);
    } else if (colDate >= 0 && colTimeOnly >= 0) {
      timeSec = parseTimeCell(cells[colTimeOnly], cells[colDate]);
    } else if (colDate >= 0) {
      // date-only — anchors to midnight
      timeSec = parseTimeCell(cells[colDate]);
    }
    if (timeSec == null) { skipped++; continue; }

    const open  = numOrNull(cells[colOpen]);
    const high  = numOrNull(cells[colHigh]);
    const low   = numOrNull(cells[colLow]);
    const close = numOrNull(cells[colClose]);
    if (open == null || high == null || low == null || close == null) { skipped++; continue; }

    const volume = colVolume >= 0 ? (numOrNull(cells[colVolume]) || 0) : 0;

    bars.push({ time: timeSec, open, high, low, close, volume });
  }

  bars.sort((a, b) => a.time - b.time);

  if (bars.length === 0) {
    return { bars: [], parsed: 0, skipped, error: `no-valid-rows (label=${label})` };
  }
  return { bars, parsed: bars.length, skipped };
};
